/**
 * Permission keys. The database `permissions` table is seeded from this list,
 * and both the API guard and the web/mobile menus use these same keys.
 *
 * Keys for later phases are included now so the client can sign off the
 * complete role × permission matrix before development reaches those phases.
 */
export const PERMISSIONS = {
  // Phase 1: masters
  BRANCHES_VIEW: 'branches.view',
  BRANCHES_MANAGE: 'branches.manage',
  USERS_VIEW: 'users.view',
  USERS_MANAGE: 'users.manage',
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',
  SUPPLIERS_VIEW: 'suppliers.view',
  SUPPLIERS_MANAGE: 'suppliers.manage',
  ITEMS_VIEW: 'items.view',
  ITEMS_MANAGE: 'items.manage',
  ITEMS_IMPORT: 'items.import',
  ITEMS_COST_VIEW: 'items.cost.view',
  STOCK_VIEW: 'stock.view',
  STOCK_OPENING_IMPORT: 'stock.opening.import',
  AUDIT_VIEW: 'audit.view',
  SETTINGS_MANAGE: 'settings.manage',

  // Phase 2: China ordering
  CHINA_ORDERS_VIEW: 'china_orders.view',
  CHINA_ORDERS_MANAGE: 'china_orders.manage',
  CHINA_ORDERS_RECEIVE: 'china_orders.receive',
  CHINA_PORTAL_ACCESS: 'china_portal.access',

  // Shipments (started in Phase 2 from the supplier invoices; tracking in Phase 3)
  SHIPMENTS_VIEW_ALL: 'shipments.view_all',
  SHIPMENTS_VIEW_LOCATION: 'shipments.view_location',
  SHIPMENTS_MANAGE: 'shipments.manage',

  // Phase 4: distribution
  STOCK_REQUESTS_CREATE: 'stock_requests.create',
  STOCK_REQUESTS_APPROVE: 'stock_requests.approve',
  TRANSFERS_DISPATCH: 'transfers.dispatch',
  TRANSFERS_ACKNOWLEDGE: 'transfers.acknowledge',
  BRANCH_ORDERS_SUPPLIER_VIEW: 'branch_orders.supplier_view',

  // Phase 5: control
  STOCK_ADJUST: 'stock.adjust',
  STOCK_ADJUST_APPROVE: 'stock.adjust.approve',
  STOCK_ACTUAL_VIEW: 'stock.actual.view',
  STOCK_DISPLAY_MANAGE: 'stock.display.manage',
  REPORTS_VIEW: 'reports.view',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export interface PermissionDefinition {
  key: PermissionKey;
  module: string;
  phase: number;
  description: string;
}

const P = PERMISSIONS;

export const PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  { key: P.BRANCHES_VIEW, module: 'Branches', phase: 1, description: 'View branches and the central warehouse' },
  { key: P.BRANCHES_MANAGE, module: 'Branches', phase: 1, description: 'Create, edit, activate and deactivate branches' },
  { key: P.USERS_VIEW, module: 'Users', phase: 1, description: 'View users' },
  { key: P.USERS_MANAGE, module: 'Users', phase: 1, description: 'Create, edit, disable users, reset passwords, revoke devices' },
  { key: P.ROLES_VIEW, module: 'Roles & permissions', phase: 1, description: 'View roles and the permission matrix' },
  { key: P.ROLES_MANAGE, module: 'Roles & permissions', phase: 1, description: 'Create roles and edit the permission matrix' },
  { key: P.SUPPLIERS_VIEW, module: 'Items', phase: 1, description: 'View suppliers' },
  { key: P.SUPPLIERS_MANAGE, module: 'Items', phase: 1, description: 'Create and edit suppliers and categories' },
  { key: P.ITEMS_VIEW, module: 'Items', phase: 1, description: 'View items and prices' },
  { key: P.ITEMS_MANAGE, module: 'Items', phase: 1, description: 'Create, edit, activate and deactivate items' },
  { key: P.ITEMS_IMPORT, module: 'Items', phase: 1, description: 'Bulk import items from Excel/CSV' },
  { key: P.ITEMS_COST_VIEW, module: 'Items', phase: 1, description: 'See and edit item cost (hidden from everyone else)' },
  { key: P.STOCK_VIEW, module: 'Stock', phase: 1, description: 'View stock by location' },
  { key: P.STOCK_OPENING_IMPORT, module: 'Stock', phase: 1, description: 'Load opening stock balances' },
  { key: P.AUDIT_VIEW, module: 'Administration', phase: 1, description: 'View the audit log' },
  { key: P.SETTINGS_MANAGE, module: 'Administration', phase: 1, description: 'Edit system settings (cut-off day, thresholds)' },

  { key: P.CHINA_ORDERS_VIEW, module: 'China ordering', phase: 2, description: 'View China order requests' },
  { key: P.CHINA_ORDERS_MANAGE, module: 'China ordering', phase: 2, description: 'Create and manage China order requests' },
  { key: P.CHINA_ORDERS_RECEIVE, module: 'China ordering', phase: 2, description: 'Record goods received from a shipment at the central warehouse or at your branch' },
  { key: P.CHINA_PORTAL_ACCESS, module: 'China ordering', phase: 2, description: 'China team portal: confirm and ship orders' },

  { key: P.SHIPMENTS_VIEW_ALL, module: 'Shipments', phase: 2, description: 'View all shipments, containers and supplier invoices' },
  { key: P.SHIPMENTS_VIEW_LOCATION, module: 'Shipments', phase: 2, description: 'View inbound shipments for own location only (all of them for central warehouse staff)' },
  { key: P.SHIPMENTS_MANAGE, module: 'Shipments', phase: 2, description: 'Import supplier invoices and edit shipments' },

  { key: P.STOCK_REQUESTS_CREATE, module: 'Distribution', phase: 4, description: 'Place shop orders for your branch with the central warehouse' },
  { key: P.STOCK_REQUESTS_APPROVE, module: 'Distribution', phase: 4, description: 'See every shop order (no approval step: the warehouse sends what it has)' },
  { key: P.TRANSFERS_DISPATCH, module: 'Distribution', phase: 4, description: 'Send stock from the central warehouse to a branch, including a received invoice\'s goods and shop orders; decline what can\'t be sent' },
  { key: P.TRANSFERS_ACKNOWLEDGE, module: 'Distribution', phase: 4, description: 'Acknowledge deliveries at a branch' },
  { key: P.BRANCH_ORDERS_SUPPLIER_VIEW, module: 'Distribution', phase: 5, description: 'Supplier logins and China team: see shop orders for a supplier\'s items (no stock or prices) and get the alert when a shop orders' },

  { key: P.STOCK_ADJUST, module: 'Stock control', phase: 7, description: 'Ask for stock adjustments and upload stock counts for your branch (applied once approved)' },
  { key: P.STOCK_ADJUST_APPROVE, module: 'Stock control', phase: 7, description: 'Approve or reject stock adjustments and stock counts, at every location' },
  { key: P.STOCK_ACTUAL_VIEW, module: 'Stock control', phase: 5, description: 'See actual stock where display stock is set lower' },
  { key: P.STOCK_DISPLAY_MANAGE, module: 'Stock control', phase: 5, description: 'Set display stock below actual stock' },
  { key: P.REPORTS_VIEW, module: 'Reports', phase: 5, description: 'View reports' },
];

/**
 * The dashboard has a section for each of these; a user with none of them has
 * nothing to show there, so clients open their first screen instead.
 */
export const DASHBOARD_PERMISSIONS: PermissionKey[] = [P.REPORTS_VIEW, P.CHINA_ORDERS_VIEW, P.CHINA_PORTAL_ACCESS, P.CHINA_ORDERS_RECEIVE];

/**
 * All a supplier login (a user linked to one supplier) can ever do, whatever
 * is ticked on its role; the API drops anything else for such a user.
 */
export const SUPPLIER_LOGIN_PERMISSIONS: PermissionKey[] = [P.BRANCH_ORDERS_SUPPLIER_VIEW];

/** Data scope a role works within. */
export const ROLE_SCOPES = ['ALL', 'BRANCH', 'SUPPLIER'] as const;
export type RoleScope = (typeof ROLE_SCOPES)[number];

export const SYSTEM_ROLES = {
  ADMIN: 'ADMIN',
  PURCHASE_MANAGER: 'PURCHASE_MANAGER',
  STORE_MANAGER: 'STORE_MANAGER',
  SALES_STAFF: 'SALES_STAFF',
  WAREHOUSE_STAFF: 'WAREHOUSE_STAFF',
  CHINA_TEAM: 'CHINA_TEAM',
  SUPPLIER: 'SUPPLIER',
} as const;

export interface RoleDefinition {
  code: string;
  name: string;
  scope: RoleScope;
  description: string;
  permissions: PermissionKey[] | '*';
}

/**
 * Default matrix from SRS v0.2 Section 3. Items marked "pending" in
 * docs/permissions.md are awaiting client confirmation (SRS Q6, Q10).
 */
export const DEFAULT_ROLES: RoleDefinition[] = [
  {
    code: SYSTEM_ROLES.ADMIN,
    name: 'Management / Admin',
    scope: 'ALL',
    description: 'Owners and senior managers. Always has every permission.',
    permissions: '*',
  },
  {
    code: SYSTEM_ROLES.PURCHASE_MANAGER,
    name: 'Purchase Manager',
    scope: 'ALL',
    description: 'Handles China ordering',
    permissions: [
      P.BRANCHES_VIEW, P.SUPPLIERS_VIEW, P.SUPPLIERS_MANAGE, P.ITEMS_VIEW, P.ITEMS_MANAGE,
      P.ITEMS_IMPORT, P.ITEMS_COST_VIEW, P.STOCK_VIEW, P.CHINA_ORDERS_VIEW, P.CHINA_ORDERS_MANAGE,
      P.CHINA_ORDERS_RECEIVE, P.SHIPMENTS_VIEW_ALL, P.SHIPMENTS_MANAGE, P.REPORTS_VIEW,
      P.TRANSFERS_DISPATCH, P.STOCK_ACTUAL_VIEW, P.STOCK_ADJUST, P.STOCK_ADJUST_APPROVE,
    ],
  },
  {
    code: SYSTEM_ROLES.STORE_MANAGER,
    name: 'Store Manager',
    scope: 'BRANCH',
    description: 'Runs a branch',
    permissions: [
      P.BRANCHES_VIEW, P.ITEMS_VIEW, P.STOCK_VIEW, P.STOCK_REQUESTS_CREATE,
      P.TRANSFERS_ACKNOWLEDGE, P.SHIPMENTS_VIEW_LOCATION, P.CHINA_ORDERS_RECEIVE,
      // client, 28 Oct 2026: Store Managers see cost and actual stock, and count their branch
      P.ITEMS_COST_VIEW, P.STOCK_ACTUAL_VIEW, P.STOCK_ADJUST,
    ],
  },
  {
    code: SYSTEM_ROLES.SALES_STAFF,
    name: 'Sales Staff',
    scope: 'BRANCH',
    description: 'Shop floor. No China shipment details.',
    permissions: [P.BRANCHES_VIEW, P.ITEMS_VIEW, P.STOCK_VIEW, P.STOCK_REQUESTS_CREATE],
  },
  {
    code: SYSTEM_ROLES.WAREHOUSE_STAFF,
    name: 'Warehouse Staff',
    scope: 'BRANCH',
    description: 'Central inventory',
    permissions: [
      P.BRANCHES_VIEW, P.ITEMS_VIEW, P.STOCK_VIEW, P.TRANSFERS_DISPATCH,
      P.SHIPMENTS_VIEW_LOCATION, P.STOCK_ADJUST, P.CHINA_ORDERS_RECEIVE,
    ],
  },
  {
    code: SYSTEM_ROLES.CHINA_TEAM,
    name: 'China Team',
    scope: 'SUPPLIER',
    description: 'Staff in China who confirm and ship orders for every supplier. No stock or reports.',
    permissions: [P.SUPPLIERS_VIEW, P.CHINA_PORTAL_ACCESS, P.SHIPMENTS_MANAGE, P.BRANCH_ORDERS_SUPPLIER_VIEW],
  },
  {
    code: SYSTEM_ROLES.SUPPLIER,
    name: 'Supplier',
    scope: 'SUPPLIER',
    description: 'A supplier\'s own staff, linked to one supplier. Told when a shop orders that supplier\'s items; sees only those lines.',
    permissions: [P.BRANCH_ORDERS_SUPPLIER_VIEW],
  },
];
